import { useEffect, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const toFr = (iso) => {
  if (!iso) return "";
  const d = parse(String(iso).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(d) ? format(d, "dd/MM/yyyy") : "";
};

// Champ date au format jj/mm/aaaa + calendrier français. value/onChange en ISO (YYYY-MM-DD), compatible avec <Input type="date">.
export const DateInputFR = ({ value, onChange, disabled, className = "", placeholder = "jj/mm/aaaa", ...rest }) => {
  const [text, setText] = useState(toFr(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(toFr(value)); }, [value]);

  const emit = (iso) => onChange?.({ target: { value: iso } });
  const onType = (raw) => {
    let v = raw.replace(/[^\d/]/g, "").replace(/\/+/g, "/");
    const typedDigit = /\d$/.test(raw) && raw.length > text.length;
    if (typedDigit && (/^\d{2}$/.test(v) || /^\d{2}\/\d{2}$/.test(v))) { v = `${v}/`; }
    v = v.slice(0, 10);
    setText(v);
    if (!v) { emit(""); return; }
    const d = parse(v, "dd/MM/yyyy", new Date());
    if (v.length === 10 && isValid(d)) emit(format(d, "yyyy-MM-dd"));
  };
  const selected = value ? parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date()) : undefined;
  const year = new Date().getFullYear();

  return (
    <div className={`relative ${className}`}>
      <input
        type="text" inputMode="numeric" value={text} placeholder={placeholder} disabled={disabled}
        onChange={(e) => onType(e.target.value)} onBlur={() => setText(toFr(value))}
        className="flex h-9 w-full rounded-md border border-input bg-transparent pl-3 pr-9 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 font-mono"
        {...rest}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} aria-label="Ouvrir le calendrier"
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}>
            <CalendarDays className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar mode="single" locale={fr} weekStartsOn={1} selected={selected && isValid(selected) ? selected : undefined}
            defaultMonth={selected && isValid(selected) ? selected : undefined}
            captionLayout="dropdown-buttons" fromYear={1920} toYear={year + 15}
            classNames={{
              caption_label: "hidden", vhidden: "sr-only", caption_dropdowns: "flex gap-1.5 items-center",
              dropdown_month: "relative", dropdown_year: "relative", dropdown_icon: "hidden",
              dropdown: "rounded-md border border-slate-200 bg-white px-1.5 py-0.5 text-sm cursor-pointer focus:outline-none focus:ring-1 focus:ring-slate-400",
            }}
            onSelect={(d) => { if (d) { emit(format(d, "yyyy-MM-dd")); setOpen(false); } }} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
};
